import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Plus, Pencil, Trash2, ArrowLeft, Save } from "lucide-react";
import { Logo } from "../../components/Logo";
import { API, authHeaders, formatError } from "../../lib/adminApi";

const emptyForm = { email: "", name: "", password: "", otp: "", active: true };

export default function AdminTeamUsers() {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // { id? , ...form }
  const [err, setErr] = useState("");

  const load = async () => {
    try {
      const r = await axios.get(`${API}/admin/team-users`, { headers: authHeaders() });
      setUsers(r.data || []);
    } catch (e) { setErr(formatError(e)); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const save = async () => {
    setErr("");
    try {
      if (editing.id) {
        const payload = {};
        if (editing.name) payload.name = editing.name;
        if (editing.password) payload.password = editing.password;
        if (editing.otp) payload.otp = editing.otp;
        if (typeof editing.active === "boolean") payload.active = editing.active;
        await axios.put(`${API}/admin/team-users/${editing.id}`, payload, { headers: authHeaders() });
      } else {
        await axios.post(`${API}/admin/team-users`, editing, { headers: authHeaders() });
      }
      setEditing(null);
      await load();
    } catch (e) { setErr(formatError(e)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this team user? Their assigned CPs will be unassigned.")) return;
    await axios.delete(`${API}/admin/team-users/${id}`, { headers: authHeaders() });
    load();
  };

  return (
    <div className="min-h-screen bg-white/60">
      <header className="border-b border-slate-200 bg-white/80 backdrop-blur px-6 py-4 flex items-center justify-between sticky top-0 z-10">
        <div className="flex items-center gap-3"><Logo /> <span className="font-heading font-bold text-lg">Team Users</span></div>
        <button onClick={() => navigate("/admin/dashboard")} className="inline-flex items-center gap-1 text-sm text-slate-600 hover:text-[#FF5E1A]">
          <ArrowLeft className="w-4 h-4" /> Dashboard
        </button>
      </header>
      <div className="max-w-5xl mx-auto px-6 py-8">
        <div className="flex justify-between items-center mb-4">
          <h1 className="font-heading text-2xl font-bold">Team Members</h1>
          <button
            onClick={() => setEditing({ ...emptyForm })}
            data-testid="team-user-add-btn"
            className="inline-flex items-center gap-1 rounded-full bg-[#FF5E1A] hover:bg-[#E04F00] text-white px-4 py-2 text-sm font-semibold"
          >
            <Plus className="w-4 h-4" /> Add team user
          </button>
        </div>
        {err && <div className="text-sm text-red-600 mb-3">{err}</div>}
        {loading ? <div className="text-slate-500">Loading…</div> : (
          <div className="glass-strong rounded-2xl overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-white/60 text-[11px] uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="text-left px-4 py-3">Name</th>
                  <th className="text-left px-4 py-3">Email</th>
                  <th className="text-left px-4 py-3">Status</th>
                  <th className="text-right px-4 py-3">Actions</th>
                </tr>
              </thead>
              <tbody data-testid="team-user-list">
                {users.map((u) => (
                  <tr key={u.id} className="border-t border-slate-100">
                    <td className="px-4 py-3 font-semibold">{u.name}</td>
                    <td className="px-4 py-3 text-slate-600">{u.email}</td>
                    <td className="px-4 py-3"><span className={`text-[11px] font-semibold ${u.active ? "text-emerald-600" : "text-slate-400"}`}>{u.active ? "Active" : "Disabled"}</span></td>
                    <td className="px-4 py-3 text-right">
                      <div className="inline-flex gap-1">
                        <button onClick={() => setEditing({ id: u.id, name: u.name, password: "", otp: "", active: u.active })}
                          data-testid={`team-user-edit-${u.id}`}
                          className="p-2 rounded-lg text-slate-500 hover:text-[#FF5E1A] hover:bg-white"><Pencil className="w-4 h-4" /></button>
                        <button onClick={() => remove(u.id)} data-testid={`team-user-delete-${u.id}`}
                          className="p-2 rounded-lg text-slate-500 hover:text-red-600 hover:bg-white"><Trash2 className="w-4 h-4" /></button>
                      </div>
                    </td>
                  </tr>
                ))}
                {users.length === 0 && <tr><td colSpan="4" className="px-4 py-6 text-center text-slate-500">No team users yet.</td></tr>}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {editing && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center px-4 py-6" onClick={() => setEditing(null)}>
          <div className="bg-white rounded-3xl w-full max-w-md p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-heading font-bold text-lg mb-4">{editing.id ? "Edit team user" : "Add team user"}</h3>
            <div className="flex flex-col gap-3">
              <input type="text" placeholder="Name" value={editing.name || ""} onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                data-testid="team-user-name" className="px-3 py-2 rounded-xl glass-input text-sm" />
              {!editing.id && (
                <input type="email" placeholder="Email" value={editing.email || ""} onChange={(e) => setEditing({ ...editing, email: e.target.value })}
                  data-testid="team-user-email" className="px-3 py-2 rounded-xl glass-input text-sm" />
              )}
              <input type="text" placeholder={editing.id ? "New password (leave empty to keep)" : "Password"}
                value={editing.password || ""} onChange={(e) => setEditing({ ...editing, password: e.target.value })}
                data-testid="team-user-password" className="px-3 py-2 rounded-xl glass-input text-sm" />
              <input type="text" placeholder={editing.id ? "New OTP (leave empty to keep)" : "OTP (min 4 chars) — share with team member"}
                value={editing.otp || ""} onChange={(e) => setEditing({ ...editing, otp: e.target.value })}
                data-testid="team-user-otp" className="px-3 py-2 rounded-xl glass-input text-sm font-mono" />
              {editing.id && (
                <label className="text-xs flex items-center gap-2">
                  <input type="checkbox" checked={!!editing.active} onChange={(e) => setEditing({ ...editing, active: e.target.checked })} /> Active
                </label>
              )}
              {err && <div className="text-xs text-red-600">{err}</div>}
              <div className="flex gap-2 justify-end mt-2">
                <button onClick={() => setEditing(null)} className="px-4 py-2 text-sm rounded-xl bg-white border border-slate-300">Cancel</button>
                <button onClick={save} data-testid="team-user-save"
                  className="px-4 py-2 text-sm rounded-xl bg-[#FF5E1A] text-white font-semibold inline-flex items-center gap-1">
                  <Save className="w-4 h-4" /> Save
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
